
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export function useDashboardData() {
  const [userStats, setUserStats] = useState<any>({
    totalContracts: 0,
    activeContracts: 0,
    expiringContracts: 0,
    totalValue: 0,
    valuesByCurrency: [],
    defaultCurrencyId: null,
  });
  const [contractsTimeline, setContractsTimeline] = useState<any[]>([]);
  const [contractsDistribution, setContractsDistribution] = useState<any[]>([]);
  const [recentContracts, setRecentContracts] = useState<any[]>([]);
  const [upcomingExpirations, setUpcomingExpirations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchDashboardData = useCallback(async (selectedYear = new Date().getFullYear()) => {
    setIsLoading(true);
    try {
      const startOfYear = new Date(selectedYear, 0, 1).toISOString();
      const endOfYear = new Date(selectedYear, 11, 31, 23, 59, 59).toISOString();

      // Fetch total contracts count for the selected year
      const { count: totalCount, error: totalError } = await supabase
        .from('contracts')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startOfYear)
        .lte('created_at', endOfYear);

      if (totalError) throw totalError;

      // Fetch active contracts (contracts that have not expired) for the selected year
      const today = new Date().toISOString().split('T')[0];
      const { count: activeCount, error: activeError } = await supabase
        .from('contracts')
        .select('*', { count: 'exact', head: true })
        .gte('expiry_date', today)
        .gte('created_at', startOfYear)
        .lte('created_at', endOfYear);

      if (activeError) throw activeError;

      // Fetch contracts expiring in next 30 days that were created in the selected year
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      const thirtyDaysDate = thirtyDaysFromNow.toISOString().split('T')[0];

      const { count: expiringCount, error: expiringError } = await supabase
        .from('contracts')
        .select('*', { count: 'exact', head: true })
        .gte('expiry_date', today)
        .lte('expiry_date', thirtyDaysDate)
        .gte('created_at', startOfYear)
        .lte('created_at', endOfYear);

      if (expiringError) throw expiringError;

      // Fetch total value for contracts in the selected year
      const { data: valueData, error: valueError } = await supabase
        .from('contracts')
        .select('expected_cost, currency_id, currencies:currency_id(id, code, symbol)')
        .gte('created_at', startOfYear)
        .lte('created_at', endOfYear);

      if (valueError) throw valueError;

      // Calculate total value and organize by currency
      let totalValue = 0;
      const valuesByCurrency: Record<string, { currencyId: string, total: number, symbol: string, code: string }> = {};
      
      valueData.forEach(contract => {
        const cost = parseFloat(String(contract.expected_cost || '0'));
        totalValue += cost;
        
        const currencyId = contract.currency_id || 'unknown';
        if (!valuesByCurrency[currencyId]) {
          valuesByCurrency[currencyId] = {
            currencyId,
            total: 0,
            symbol: contract.currencies?.symbol || '$',
            code: contract.currencies?.code || 'USD'
          };
        }
        valuesByCurrency[currencyId].total += cost;
      });

      // Get user's default currency
      const { data: preferences, error: prefError } = await supabase
        .from('user_preferences')
        .select('default_currency_id')
        .single();

      if (prefError && prefError.code !== 'PGRST116') {
        console.error('Error fetching user preferences:', prefError);
      }

      const defaultCurrencyId = preferences?.default_currency_id || null;

      // Fetch contracts timeline (using signed_date grouped by month) for the selected year
      const { data: timelineData, error: timelineError } = await supabase
        .from('contracts')
        .select('signed_date')
        .gte('signed_date', `${selectedYear}-01-01`)
        .lte('signed_date', `${selectedYear}-12-31`);

      if (timelineError) throw timelineError;

      const timelineByMonth: Record<string, number> = {};
      // Initialize all months with 0 to ensure we have all months in the timeline
      for (let month = 0; month < 12; month++) {
        const date = new Date(selectedYear, month, 1);
        const monthYear = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        timelineByMonth[monthYear] = 0;
      }
      
      timelineData.forEach(contract => {
        if (contract.signed_date) {
          const date = new Date(contract.signed_date);
          const monthYear = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          timelineByMonth[monthYear] = (timelineByMonth[monthYear] || 0) + 1;
        }
      });

      // Convert timeline to array and sort chronologically
      const timeline = Object.entries(timelineByMonth)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA.getTime() - dateB.getTime();
        });

      // Fetch contract type distribution for the selected year
      const { data: typesData, error: typesError } = await supabase
        .from('contracts')
        .select('contract_type_id, contract_types(name)')
        .gte('created_at', startOfYear)
        .lte('created_at', endOfYear);

      if (typesError) throw typesError;

      const typeDistribution: Record<string, number> = {};
      typesData.forEach(contract => {
        const typeName = contract.contract_types?.name || 'Uncategorized';
        typeDistribution[typeName] = (typeDistribution[typeName] || 0) + 1;
      });

      const distribution = Object.entries(typeDistribution).map(([name, value]) => ({ name, value }));

      // Fetch recent contracts for the selected year
      const { data: recentData, error: recentError } = await supabase
        .from('contracts')
        .select('id, supplier_name, created_at, expected_cost, contract_types(name), currencies(symbol, code)')
        .gte('created_at', startOfYear)
        .lte('created_at', endOfYear)
        .order('created_at', { ascending: false })
        .limit(5);

      if (recentError) throw recentError;

      // Fetch upcoming expirations
      const { data: expiringData, error: upcomingError } = await supabase
        .from('contracts')
        .select('id, supplier_name, expiry_date, contract_types(name)')
        .gte('expiry_date', today)
        .lte('expiry_date', thirtyDaysDate)
        .gte('created_at', startOfYear)
        .lte('created_at', endOfYear)
        .order('expiry_date', { ascending: true })
        .limit(5);

      if (upcomingError) throw upcomingError;

      // Update all state variables with fetched data
      setUserStats({
        totalContracts: totalCount || 0,
        activeContracts: activeCount || 0,
        expiringContracts: expiringCount || 0,
        totalValue: totalValue,
        valuesByCurrency: Object.values(valuesByCurrency),
        defaultCurrencyId
      });
      setContractsTimeline(timeline);
      setContractsDistribution(distribution);
      setRecentContracts(recentData || []);
      setUpcomingExpirations(expiringData || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    userStats,
    contractsTimeline,
    contractsDistribution,
    recentContracts,
    upcomingExpirations,
    isLoading,
    fetchDashboardData
  };
}
